// Команды правки, оформления и стилей СТО, перемещения блоков, вида; вкладки «Главная» и «Вид» ленты.
import type { Editor } from '@tiptap/core';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { RegistryApi, Ctx } from '../../core/registry';
import { appStore } from '../../core/store';
import { uiStore } from '../../ui/uiState';
import { askText } from '../../ui/prompts';
import { StylesGallery, FontInfo } from './StylesGallery';
import { setListNumbering } from '../../core/schema/lists';

const ed = (c: Ctx) => c.editor?.chain().focus();
const isMark = (c: Ctx, m: string) => !!c.editor?.isActive(m);
const inType = (c: Ctx, t: string, attrs?: Record<string, unknown>) => !!c.editor?.isActive(t, attrs);

// ------------------------------------------------------------------------------------------------ масштаб
export const ZOOM_MIN = 0.3;
export const ZOOM_MAX = 3;
export function setZoom(z: number, mode: 'fit' | 'manual' = 'manual') {
  appStore.getState().set({ zoom: Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(z * 100) / 100)) });
  if (uiStore.getState().zoomMode !== mode) uiStore.getState().set({ zoomMode: mode });
}
/** Ширина листа A4 при 100 % (px CSS). */
export const SHEET_W = (210 / 25.4) * 96;
export const SHEET_H = (297 / 25.4) * 96;
export function fitWidthZoom(): number | null {
  const c = document.querySelector('.canvas') as HTMLElement | null;
  if (!c) return null;
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, (c.clientWidth - 56) / SHEET_W));
}
export function zoomStep(dir: 1 | -1) {
  const steps = [0.3, 0.4, 0.5, 0.6, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3];
  const z = appStore.getState().zoom;
  const next = dir > 0 ? steps.find((s) => s > z + 0.001) : [...steps].reverse().find((s) => s < z - 0.001);
  setZoom(next ?? z);
}

/** После смены раскладки (фокус, лента) - текущий абзац снова посередине экрана. */
export function keepCaretInView(c: Ctx) {
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const e = c.editor;
    if (!e || e.isDestroyed) return;
    try {
      const dom = e.view.domAtPos(e.state.selection.from).node as HTMLElement;
      const el = (dom.nodeType === 3 ? dom.parentElement : dom) as HTMLElement | null;
      el?.scrollIntoView({ block: 'center' });
    } catch { /* нет узла */ }
  }));
}

// ------------------------------------------------------------------------------------------------ блоки
/** Ближайший «перемещаемый» блок у курсора: блок верхнего уровня (в документе или приложении) или позиция
 *  перечисления. at - точная позиция начала блока (ручка блока), иначе - по выделению. */
export function movableAt(editor: Editor, at?: number): { pos: number; node: PMNode; parent: PMNode; index: number } | null {
  const { doc } = editor.state;
  if (at !== undefined) {
    const node = doc.nodeAt(at);
    if (!node) return null;
    const $a = doc.resolve(at);
    return { pos: at, node, parent: $a.parent, index: $a.index() };
  }
  const sel = editor.state.selection;
  if (sel instanceof NodeSelection && ['doc', 'appendix', 'stoList'].includes(sel.$from.parent.type.name)) {
    return { pos: sel.from, node: sel.node, parent: sel.$from.parent, index: sel.$from.index() };
  }
  const $p = sel.$from;
  for (let d = $p.depth; d > 0; d--) {
    const parent = $p.node(d - 1);
    if (['doc', 'appendix', 'stoList'].includes(parent.type.name)) {
      return { pos: $p.before(d), node: $p.node(d), parent, index: $p.index(d - 1) };
    }
  }
  return null;
}

export function moveBlock(editor: Editor, dir: -1 | 1, at?: number): boolean {
  const m = movableAt(editor, at);
  if (!m) return false;
  const { node, parent, index, pos } = m;
  const j = index + dir;
  if (j < 0 || j >= parent.childCount) return false;
  const other = parent.child(j);
  const kind = (n: PMNode) => (n.type.name === 'appendix' ? 'app' : n.type.name === 'appendixTitle' ? 'title' : 'block');
  if (kind(other) !== kind(node) || kind(node) === 'title') return false;
  const { state } = editor;
  const sel = state.selection;
  const offset = sel.from - pos;
  // курсор внутри блока - остаётся на том же месте текста; иначе блок выделяется целиком
  const keepText = !(sel instanceof NodeSelection) && sel.from > pos && sel.to < pos + node.nodeSize;
  const tr = state.tr;
  let np: number;
  if (dir < 0) {
    np = pos - other.nodeSize;
    tr.delete(pos, pos + node.nodeSize).insert(np, node);
  } else {
    const after = pos + node.nodeSize;
    tr.delete(after, after + other.nodeSize).insert(pos, other);
    np = pos + other.nodeSize;
  }
  tr.setSelection(keepText ? TextSelection.near(tr.doc.resolve(Math.min(np + offset, tr.doc.content.size))) : NodeSelection.create(tr.doc, np));
  editor.view.dispatch(tr.scrollIntoView());
  editor.view.focus();
  return true;
}

export function duplicateBlock(editor: Editor, at?: number) {
  const m = movableAt(editor, at);
  if (!m || m.node.type.name === 'appendixTitle') return;
  const end = m.pos + m.node.nodeSize;
  editor.view.dispatch(editor.state.tr.insert(end, m.node.copy(m.node.content)).scrollIntoView());   // id заменит normalize
}

export function deleteBlock(editor: Editor, at?: number) {
  const m = movableAt(editor, at);
  if (!m || m.node.type.name === 'appendixTitle') return;
  const tr = editor.state.tr.delete(m.pos, m.pos + m.node.nodeSize);
  if (tr.doc.childCount === 0) tr.insert(0, editor.schema.nodes.paragraph.create());
  editor.view.dispatch(tr.scrollIntoView());
}

/** Сделать текущий блок другим «стилем»: текст, раздел/подраздел/пункт, перечисление. */
export function turnInto(c: Ctx, what: string) {
  const e = c.editor;
  if (!e) return;
  const inList = e.isActive('stoList');
  if (what === 'paragraph' || /^h[123]$/.test(what)) {
    let ch = e.chain().focus();
    if (inList) ch = ch.liftListItem('stoListItem');
    if (what === 'paragraph') ch.setNode('paragraph').run();
    else ch.setNode('heading', { level: Number(what[1]) }).run();
    return;
  }
  if (['dash', 'letter', 'digit'].includes(what)) {
    if (e.isActive('heading')) e.chain().focus().setNode('paragraph').run();
    (e.chain().focus() as any).setStoList(what).run();
    return;
  }
  c.run(what === 'note' ? 'insert.note' : what === 'example' ? 'insert.example' : what);
}

// ------------------------------------------------------------------------------------------------ буфер обмена
/** Вставка из системного буфера по кнопке/меню (не Ctrl+V): чтение буфера разрешает браузер. */
export async function pasteFromClipboard(c: Ctx, plain: boolean) {
  const e = c.editor;
  if (!e) return;
  const nav = navigator.clipboard as any;
  try {
    if (plain || !nav?.read) {
      const text: string = await navigator.clipboard.readText();
      if (!text) return;
      insertPlainText(e, text);
      return;
    }
    const items = await nav.read();
    const dt = new DataTransfer();
    for (const it of items) {
      for (const t of it.types as string[]) {
        const blob: Blob = await it.getType(t);
        if (t.startsWith('image/')) dt.items.add(new File([blob], 'image.' + (t.split('/')[1] || 'png'), { type: t }));
        else dt.setData(t, await blob.text());
      }
    }
    e.view.focus();
    e.view.dom.dispatchEvent(new ClipboardEvent('paste', { clipboardData: dt, bubbles: true, cancelable: true }));
  } catch {
    c.toast('Браузер не дал прочитать буфер обмена. Используйте Ctrl+V (или Ctrl+Shift+V - только текст).', 'warn', 6000);
  }
}

/** Вставить обычный текст: строки -> абзацы (без оформления). */
export function insertPlainText(e: Editor, text: string) {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  if (lines.length === 1) { e.chain().focus().insertContent({ type: 'text', text: lines[0] }).run(); return; }
  const paras = lines.map((l) => (l.trim() ? { type: 'paragraph', content: [{ type: 'text', text: l }] } : { type: 'paragraph' }));
  e.chain().focus().insertContent(paras).run();
}

function execClipboard(c: Ctx, cmd: 'cut' | 'copy') {
  c.editor?.view.focus();
  let ok = false;
  try { ok = document.execCommand(cmd); } catch { ok = false; }
  if (!ok) c.toast(cmd === 'cut' ? 'Используйте Ctrl+X' : 'Используйте Ctrl+C', 'info');
}

// ------------------------------------------------------------------------------------------------ регистрация
export function setupCommands(r: RegistryApi) {
  // --- правка
  r.commands.add({ id: 'edit.undo', title: 'Отменить', icon: 'undo', shortcut: 'Mod-z', run: (c) => ed(c)?.undo().run(), isEnabled: (c) => !!c.editor?.can().undo() });
  r.commands.add({ id: 'edit.redo', title: 'Вернуть', icon: 'redo', shortcut: 'Mod-y', run: (c) => ed(c)?.redo().run(), isEnabled: (c) => !!c.editor?.can().redo() });
  r.commands.add({ id: 'edit.cut', title: 'Вырезать', description: 'Ctrl+X', icon: 'cut', run: (c) => execClipboard(c, 'cut'),
    isEnabled: (c) => !!c.editor && !c.editor.state.selection.empty });
  r.commands.add({ id: 'edit.copy', title: 'Копировать', description: 'Ctrl+C', icon: 'copy', run: (c) => execClipboard(c, 'copy'),
    isEnabled: (c) => !!c.editor && !c.editor.state.selection.empty });
  r.commands.add({ id: 'edit.paste', title: 'Вставить', description: 'Из буфера обмена: текст и таблицы из Word, картинки, формулы', icon: 'paste',
    run: (c) => pasteFromClipboard(c, false) });
  r.commands.add({ id: 'edit.pasteText', title: 'Вставить только текст', description: 'Без оформления исходного документа', icon: 'text',
    run: (c) => pasteFromClipboard(c, true) });
  r.commands.add({ id: 'edit.selectAll', title: 'Выделить всё', icon: 'cursor', run: (c) => ed(c)?.selectAll().run() });
  r.commands.add({ id: 'block.moveUp', title: 'Переместить блок вверх', description: 'Абзац, рисунок, таблицу или пункт перечисления', icon: 'up',
    shortcut: 'Alt-Shift-ArrowUp', run: (c) => c.editor && moveBlock(c.editor, -1) });
  r.commands.add({ id: 'block.moveDown', title: 'Переместить блок вниз', icon: 'arrow-down', shortcut: 'Alt-Shift-ArrowDown',
    run: (c) => c.editor && moveBlock(c.editor, 1) });
  r.commands.add({ id: 'block.duplicate', title: 'Дублировать блок', icon: 'duplicate', shortcut: 'Mod-Shift-d', run: (c) => c.editor && duplicateBlock(c.editor) });
  r.commands.add({ id: 'block.delete', title: 'Удалить блок', icon: 'trash', shortcut: 'Mod-Shift-Backspace', run: (c) => c.editor && deleteBlock(c.editor) });
  r.commands.add({ id: 'block.turnInto', title: 'Превратить блок в…', icon: 'paragraph', run: (c, a) => turnInto(c, String(a || 'paragraph')) });

  // --- шрифт (только то, что допускает СТО: гарнитура и размер задаются сборщиком)
  const marks: [string, string, string, string, string, string][] = [
    ['bold', 'Полужирный', 'bold', 'Mod-b', 'toggleBold', 'По профилю - только головка таблицы (заголовки полужирные сами); в тексте при экспорте снимается'],
    ['italic', 'Курсив', 'italic', 'Mod-i', 'toggleItalic', 'Выделение слов и латинских обозначений'],
    ['underline', 'Подчёркнутый', 'underline', 'Mod-u', 'toggleUnderline', ''],
    ['superscript', 'Верхний индекс', 'superscript', 'Mod-.', 'toggleSuperscript', 'м² , 10⁻³'],
    ['subscript', 'Нижний индекс', 'subscript', 'Mod-,', 'toggleSubscript', 'H₂O, U₁'],
    ['code', 'Код в строке', 'code', 'Mod-e', 'toggleCode', 'Моноширинный шрифт для команд и имён файлов'],
  ];
  for (const [m, title, icon, key, fn, desc] of marks) {
    r.commands.add({ id: 'format.' + m, title, icon, shortcut: key, description: desc || undefined,
      run: (c) => (ed(c) as any)?.[fn]().run(), isActive: (c) => isMark(c, m) });
  }
  r.commands.add({ id: 'format.clear', title: 'Очистить оформление', description: 'Снять полужирный, курсив, индексы и ссылки с выделения',
    icon: 'eraser', shortcut: 'Mod-Space', run: (c) => ed(c)?.unsetAllMarks().run() });
  r.commands.add({ id: 'format.link', title: 'Гиперссылка', description: 'Адрес сайта в тексте (для списка источников - «Ссылки → Источник»)',
    icon: 'link', shortcut: 'Mod-Shift-k', isActive: (c) => isMark(c, 'link'),
    run: async (c) => {
      const e = c.editor;
      if (!e) return;
      const prev = e.getAttributes('link').href || '';
      const url = await askText({ title: prev ? 'Изменить гиперссылку' : 'Гиперссылка', label: 'Адрес (пусто - убрать ссылку)',
        value: prev || 'https://', allowEmpty: true, ok: 'Готово', testid: 'link-dialog' });
      if (url === undefined) return;
      if (!url.trim() || url.trim() === 'https://') e.chain().focus().extendMarkRange('link').unsetLink().run();
      else if (e.state.selection.empty && !e.isActive('link')) {
        e.chain().focus().insertContent({ type: 'text', text: url.trim(), marks: [{ type: 'link', attrs: { href: url.trim() } }] }).run();
      } else e.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
    } });

  // --- стили абзаца (только разрешённые СТО)
  r.commands.add({ id: 'block.paragraph', title: 'Обычный текст', description: 'Абзац: отступ 1,25 см, по ширине, 14 пт, интервал 1,5',
    icon: 'paragraph', shortcut: 'Mod-Alt-0', run: (c) => turnInto(c, 'paragraph'), isActive: (c) => inType(c, 'paragraph') && !inType(c, 'stoList') });
  const hs = ['', 'Раздел (заголовок 1)', 'Подраздел (заголовок 2)', 'Пункт (заголовок 3)'];
  const hd = ['', 'Номер «1» ставится сам; раздел начинается с новой страницы', 'Номер «1.1» ставится сам', 'Номер «1.1.1» ставится сам'];
  for (const l of [1, 2, 3]) {
    r.commands.add({ id: 'block.h' + l, title: hs[l], description: hd[l], icon: 'h' + l, shortcut: 'Mod-Alt-' + l,
      run: (c) => turnInto(c, 'h' + l), isActive: (c) => inType(c, 'heading', { level: l }) });
  }
  const lists: [string, string, string, string, string][] = [
    ['dash', 'Перечисление «-»', 'list', 'Mod-Shift-l', 'Дефис «-» с абзацного отступа (4.4)'],
    ['letter', 'Перечисление «а)»', 'list-tree', 'Mod-Shift-9', 'Строчные буквы без ё, з, й, о, ч, ъ, ы, ь'],
    ['digit', 'Перечисление «1)»', 'list-digit', 'Mod-Shift-7', 'Арабские цифры со скобкой - для вложенного перечисления (4.4.4)'],
  ];
  for (const [k, title, icon, key, desc] of lists) {
    r.commands.add({ id: 'list.' + k, title, icon, shortcut: key, description: desc,
      run: (c) => {
        if (inType(c, 'stoList', { kind: k })) { ed(c)?.liftListItem('stoListItem').run(); return; }
        // 4.4.4: «1)» - только для детализации вложенного перечисления; на первом уровне - «а)»
        if (k === 'digit' && c.editor && !c.editor.isActive('stoList') && !c.editor.isActive('tableCell') && !c.editor.isActive('tableHeader')) {
          c.toast('«1)» по СТО - только для вложенного перечисления (4.4.4): сделано «а)»; «1)» получится уровнем ниже (Tab)', 'info', 5000);
          turnInto(c, 'letter');
          return;
        }
        turnInto(c, k);
      }, isActive: (c) => inType(c, 'stoList', { kind: k }) });
  }
  r.commands.add({ id: 'list.indent', title: 'Уровень ниже', description: 'Вложенное перечисление (Tab)', icon: 'plus',
    run: (c) => {
      const e = c.editor;
      if (!e) return;
      const { $from } = e.state.selection;
      let depth = 0;
      for (let d = $from.depth; d > 0; d--) if ($from.node(d).type.name === 'stoList') depth++;
      if (depth >= 2) { c.toast('Третий уровень перечисления СТО не предусматривает (4.4.4)', 'info'); return; }
      ed(c)?.sinkListItem('stoListItem').run();
    }, isEnabled: (c) => !!c.editor?.can().sinkListItem('stoListItem') });
  r.commands.add({ id: 'list.outdent', title: 'Уровень выше', description: 'Shift+Tab', icon: 'minus',
    run: (c) => ed(c)?.liftListItem('stoListItem').run(), isEnabled: (c) => !!c.editor?.can().liftListItem('stoListItem') });
  // перечисление, разорванное рисунком/формулой/таблицей: «в)» после «б)» - продолжение предыдущего (4.4.4 - позиции
  // по порядку); импорт .docx ставит его сам по нумерации Word
  r.commands.add({ id: 'list.continue', title: 'Продолжить нумерацию', description: 'Перечисление продолжает предыдущее («в)» после «б)»)',
    icon: 'list-tree', isEnabled: (c) => !!c.editor?.isActive('stoList'),
    run: (c) => { if (c.editor && !setListNumbering(c.editor)) c.toast('Выше в этом разделе нет перечисления, которое можно продолжить', 'info'); } });
  r.commands.add({ id: 'list.restart', title: 'Начать нумерацию заново', description: 'Перечисление с «а)» / «1)»',
    icon: 'list-tree', isEnabled: (c) => !!c.editor?.isActive('stoList'), run: (c) => { if (c.editor) setListNumbering(c.editor, true); } });

  // --- вид
  r.commands.add({ id: 'view.zoomIn', title: 'Крупнее', icon: 'zoom-in', shortcut: 'Mod-=', run: () => zoomStep(1) });
  r.commands.add({ id: 'view.zoomOut', title: 'Мельче', icon: 'zoom-out', shortcut: 'Mod--', run: () => zoomStep(-1) });
  r.commands.add({ id: 'view.zoom100', title: 'Масштаб 100 %', icon: 'scan', shortcut: 'Mod-0', run: () => setZoom(1) });
  r.commands.add({ id: 'view.zoomFit', title: 'По ширине', description: 'Лист во всю ширину окна', icon: 'maximize',
    run: () => { const z = fitWidthZoom(); if (z) setZoom(z, 'fit'); }, isActive: () => uiStore.getState().zoomMode === 'fit' });
  r.commands.add({ id: 'view.zoomPage', title: 'Страница целиком', icon: 'minimize', run: () => {
    const c = document.querySelector('.canvas') as HTMLElement | null;
    if (c) setZoom(Math.min((c.clientHeight - 60) / 1123, (c.clientWidth - 56) / SHEET_W));
  } });
  const toggle = (id: string, title: string, icon: string, key: 'showMarks' | 'showRuler' | 'showPages', desc: string, shortcut?: string) =>
    r.commands.add({ id, title, icon, description: desc, shortcut, run: () => uiStore.getState().set({ [key]: !uiStore.getState()[key] } as any),
      isActive: () => !!uiStore.getState()[key] });
  toggle('view.marks', 'Непечатаемые знаки', 'paragraph', 'showMarks', 'Показать ¶, пробелы · и неразрывные пробелы °', 'Mod-Shift-8');
  toggle('view.ruler', 'Линейка', 'ruler', 'showRuler', 'Поля по СТО: левое 25 мм, правое 15 мм, отступ 1,25 см');
  toggle('view.pages', 'Границы страниц', 'pagebreak', 'showPages', 'Примерные разрывы страниц с номерами (точно - в «Предпросмотре»)');
  r.commands.add({ id: 'view.focus', title: 'Режим «Фокус»', description: 'Только текст: без ленты и панелей, текущий абзац выделен. Выход - Esc',
    icon: 'focus', shortcut: 'Mod-Shift-f', run: (c) => { uiStore.getState().set({ focusMode: !uiStore.getState().focusMode }); keepCaretInView(c); },
    isActive: () => uiStore.getState().focusMode });
  r.commands.add({ id: 'view.ribbon', title: 'Свернуть ленту', description: 'Оставить только вкладки', icon: 'ribbon-collapse', shortcut: 'Mod-F1',
    run: () => uiStore.getState().set({ ribbonCollapsed: !uiStore.getState().ribbonCollapsed }), isActive: () => uiStore.getState().ribbonCollapsed });
  r.commands.add({ id: 'view.theme', title: 'Тёмная тема', description: 'Тёмный интерфейс; лист документа остаётся белым',
    icon: 'theme', run: (_c, a) => {
      const t = (a as any) || (document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark');
      uiStore.getState().set({ theme: t });
    }, isActive: () => document.documentElement.dataset.theme === 'dark' });
  const panel = (side: 'left' | 'right', id: string) => {
    const p = appStore.getState().panels;
    appStore.getState().set({ panels: { ...p, [side]: p[side] === id ? null : id } });
  };
  r.commands.add({ id: 'view.structure', title: 'Структура документа', description: 'Разделы, рисунки, таблицы - переход по щелчку',
    icon: 'list-tree', shortcut: 'Mod-Alt-n', run: () => panel('left', 'structure'), isActive: () => appStore.getState().panels.left === 'structure' });

  // --- лента: «Главная»
  r.ribbonGroups.add({ id: 'home.clip', tab: 'home', title: 'Буфер обмена', order: 5 });
  r.ribbonGroups.add({ id: 'home.edit', tab: 'home', title: 'Правка', order: 10 });
  r.ribbonGroups.add({ id: 'home.font', tab: 'home', title: 'Шрифт', order: 20 });
  r.ribbonGroups.add({ id: 'home.para', tab: 'home', title: 'Абзац', order: 30 });
  r.ribbonGroups.add({ id: 'home.styles', tab: 'home', title: 'Стили СТО', order: 40 });
  r.ribbonItems.add({ id: 'paste', group: 'home.clip', type: 'split', command: 'edit.paste', icon: 'paste', label: 'Вставить', items: [
    { command: 'edit.paste' }, { command: 'edit.pasteText' },
  ] });
  r.ribbonItems.add({ id: 'cut', group: 'home.clip', type: 'button', command: 'edit.cut', order: 1 });
  r.ribbonItems.add({ id: 'copy', group: 'home.clip', type: 'button', command: 'edit.copy', order: 2 });
  r.ribbonItems.add({ id: 'undo', group: 'home.edit', type: 'button', command: 'edit.undo' });
  r.ribbonItems.add({ id: 'redo', group: 'home.edit', type: 'button', command: 'edit.redo' });
  r.ribbonItems.add({ id: 'find', group: 'home.edit', type: 'button', command: 'edit.find', label: 'Найти' });
  r.ribbonItems.add({ id: 'replace', group: 'home.edit', type: 'button', command: 'edit.replace', label: 'Заменить' });
  r.ribbonItems.add({ id: 'font-info', group: 'home.font', type: 'custom', order: -1, render: () => <FontInfo /> });
  ['bold', 'italic', 'underline', 'superscript', 'subscript', 'code', 'link', 'clear'].forEach((m, i) =>
    r.ribbonItems.add({ id: 'mark-' + m, group: 'home.font', type: m === 'clear' || m === 'link' ? 'button' : 'toggle', command: 'format.' + m, order: i }));
  ['dash', 'letter', 'digit'].forEach((k, i) => r.ribbonItems.add({ id: 'list-' + k, group: 'home.para', type: 'toggle', command: 'list.' + k, order: i }));
  r.ribbonItems.add({ id: 'list-outdent', group: 'home.para', type: 'button', command: 'list.outdent', order: 10 });
  r.ribbonItems.add({ id: 'list-indent', group: 'home.para', type: 'button', command: 'list.indent', order: 11 });
  r.ribbonItems.add({ id: 'marks', group: 'home.para', type: 'toggle', command: 'view.marks', order: 20 });
  r.ribbonItems.add({ id: 'move-up', group: 'home.para', type: 'button', command: 'block.moveUp', order: 21 });
  r.ribbonItems.add({ id: 'move-down', group: 'home.para', type: 'button', command: 'block.moveDown', order: 22 });
  r.ribbonItems.add({ id: 'styles', group: 'home.styles', type: 'custom', render: (c) => <StylesGallery ctx={c} /> });

  // --- лента: «Вид»
  r.ribbonGroups.add({ id: 'view.mode', tab: 'view', title: 'Режим', order: 5 });
  r.ribbonGroups.add({ id: 'view.show', tab: 'view', title: 'Показать', order: 8 });
  r.ribbonGroups.add({ id: 'view.zoom', tab: 'view', title: 'Масштаб', order: 10 });
  r.ribbonGroups.add({ id: 'view.pages', tab: 'view', title: 'Страницы', order: 20 });
  r.ribbonGroups.add({ id: 'view.look', tab: 'view', title: 'Оформление', order: 30 });
  r.ribbonItems.add({ id: 'focus', group: 'view.mode', type: 'large', command: 'view.focus', label: 'Фокус' });
  r.ribbonItems.add({ id: 'structure', group: 'view.mode', type: 'large', command: 'view.structure', label: 'Структура' });
  r.ribbonItems.add({ id: 'show-ruler', group: 'view.show', type: 'toggle', command: 'view.ruler', label: 'Линейка' });
  r.ribbonItems.add({ id: 'show-pages', group: 'view.show', type: 'toggle', command: 'view.pages', label: 'Границы страниц' });
  r.ribbonItems.add({ id: 'show-marks', group: 'view.show', type: 'toggle', command: 'view.marks', label: 'Знаки ¶' });
  r.ribbonItems.add({ id: 'zoom-100', group: 'view.zoom', type: 'large', command: 'view.zoom100', label: '100 %' });
  r.ribbonItems.add({ id: 'zoom-fit', group: 'view.zoom', type: 'large', command: 'view.zoomFit' });
  r.ribbonItems.add({ id: 'zoom-page', group: 'view.zoom', type: 'large', command: 'view.zoomPage', label: 'Страница' });
  r.ribbonItems.add({ id: 'zoom-in', group: 'view.zoom', type: 'button', command: 'view.zoomIn' });
  r.ribbonItems.add({ id: 'zoom-out', group: 'view.zoom', type: 'button', command: 'view.zoomOut' });
  r.ribbonItems.add({ id: 'theme', group: 'view.look', type: 'toggle', command: 'view.theme', label: 'Тёмная тема' });
  r.ribbonItems.add({ id: 'ribbon-collapse', group: 'view.look', type: 'toggle', command: 'view.ribbon', label: 'Свернуть ленту' });
}
